import { Component, ErrorInfo, ReactNode } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { isChunkLoadError } from 'src/utils/chunk-load-error';

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
  nonChunkError: Error | null;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false, nonChunkError: null };
  }

  static getDerivedStateFromError(err: unknown): Partial<State> {
    if (isChunkLoadError(err)) {
      return { hasChunkError: true };
    }
    return { nonChunkError: err instanceof Error ? err : new Error(String(err)) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      console.warn('ChunkErrorBoundary: chunk failed to load', error.message);
    }
  }

  render() {
    const { hasChunkError, nonChunkError } = this.state;
    const { children } = this.props;

    // Rethrowing during render hands non-chunk errors to the outer boundaries;
    // a commit-phase rethrow from componentDidCatch would not reach them.
    if (nonChunkError) {
      throw nonChunkError;
    }

    if (hasChunkError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          minHeight={300}
          gap={2}
          px={3}
          textAlign="center"
        >
          <Typography variant="h6">A new version is available</Typography>
          <Typography variant="body2" color="text.secondary">
            Please refresh the page to continue.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </Box>
      );
    }

    return children;
  }
}
